<template>
    <!-- PRODUCT LD+JSON-TAG -->
</template>
<script lang="ts" setup>
    import type {
        WithContext,
        ProductGroup as ProductGroupSchema,
        Product as ProductSchema,
        Offer as ProductOffer,
        Review as ProductReview,
        MerchantReturnPolicy,
        ItemAvailability,
        ImageObject
    } from 'schema-dts';
    import type {
        ReviewsRawResponse,
        Product,
        Sku,
        FullImage
    } from '~/types';
    import { ProductAvailabilityStatus } from '~/types';
    import {
        useFullImage,
        useSkusUtils
    } from '~/composables';
    import { pathJoin } from 'x1-base/utils';
    import { isError as lo_isError } from 'es-toolkit';

    type GenerateTagProps = {
        reviewObj: Nullable<ReviewsRawResponse>;
    };

    const props = defineProps<{
        product: Product;
    }>();

    const strOutput = useState<Nullable<string>>('keyProdJsonLdtTag', () => null);

    useHead(() => {
        if (!strOutput.value) {
            return {};
        }

        return {
            script: [
                {
                    type: 'application/ld+json',
                    children: strOutput.value
                }
            ]
        };
    });

    const route = useRoute();
    const $config = useRuntimeConfig();
    const {
        title: brandTitle,
        fqdn
    } = $config.public.brand;
    const { getSingleImageUrlFromFullImage } = useFullImage();
    const { getColorOptFromSkuOptUids } = useSkusUtils();
    const { getReviews } = useReview();

    await callOnce(async () => {
        // console.time('generate-tag');
        const reviewsResp = await getReviews({}, { page_size: 1 });
        const reviewObj: Nullable<ReviewsRawResponse> = (!lo_isError(reviewsResp)) ? reviewsResp : null;
        strOutput.value = generateTag({ reviewObj });
        // console.timeEnd('generate-tag');
    });

    function generateTag(options: GenerateTagProps): Nullable<string> {
        const {
            description,
            tagline: slogan,
            name,
            uid: productUid,
            path: productPath
        } = props.product;

        const hasVariant = getSkus();
        
        const jsonLdObj: [
            WithContext<ProductGroupSchema>,
            WithContext<MerchantReturnPolicy>
        ] = [
            {
                '@context': 'https://schema.org',
                '@type': 'ProductGroup',
                url: productPath ? `https://${pathJoin(fqdn, productPath)}` : '',
                productGroupID: productUid,
                name,
                description,
                slogan,
                hasVariant,
                brand: {
                    '@type': 'Brand',
                    name: brandTitle
                },
                // TODO: This probably should be computed
                variesBy: [
                    // 'https://schema.org/size',
                    'https://schema.org/color'
                ],
                review: getReviewObj(options.reviewObj)
            },
            getReturnPolicyObj()
        ];

        try {
            return JSON.stringify(jsonLdObj);
        } catch (error) {
            console.info('Failed to stringify JSON LD OBJ');
            return null;
        }
    }

    function getSkus(): ProductSchema[] {
        const variants: ProductSchema[] = props.product.skus.map((sku: Sku) => {
            return getSkuItem(sku);
        });

        return variants;
    }

    function getSkuItem(sku: Sku): ProductSchema {
        const hasMerchantReturnPolicy = {
            '@id': '#return_policy'
        };

        const gtin: ProductOffer['gtin'] = `${sku.upc}`;

        const offers: ProductOffer = {
            '@type': 'Offer',
            gtin,
            category: sku.categoryName,
            url: sku.path ? `https://${pathJoin(fqdn, sku.path)}` : '',
            priceCurrency: 'USD',
            price: sku.price,
            availability: getItemPropStatus(sku.status),
            hasMerchantReturnPolicy
        };

        if (sku.isOnSale) {
            offers.price = sku.userPrice;
            offers.priceValidUntil = '2029-12-31';
        }

        const obj: ProductSchema = {
            '@type': 'Product',
            name: sku.seoTitle ?? sku.name,
            // Placeholder for future property
            description: sku.seoDescription ?? sku.detail,
            sku: sku.itemNo,
            color: sku.colorName ?? tryGetColor(sku),
            offers,
            hasMerchantReturnPolicy
        };

        if (sku.images?.length) {
            obj.image = getImages(sku.images);
        }

        return obj;
    }
    
    function getReturnPolicyObj(): WithContext<MerchantReturnPolicy> {
        return {
            '@context': 'https://schema.org',
            '@type': 'MerchantReturnPolicy',
            '@id': '#return_policy',
            applicableCountry: 'US',
            returnPolicyCategory: 'https://schema.org/MerchantReturnFiniteReturnWindow',
            merchantReturnDays: 30,
            returnMethod: 'https://schema.org/ReturnByMail',
            returnFees: 'https://schema.org/FreeReturn'
        }
    }

    function getItemPropStatus(status: Nullable<ProductAvailabilityStatus>): ItemAvailability {
        switch (status) {
            case ProductAvailabilityStatus.Preorder:
            case ProductAvailabilityStatus.ComingSoon:
                return 'PreOrder';
            case ProductAvailabilityStatus.SoldOut:
                return 'OutOfStock';
            case ProductAvailabilityStatus.Backorder:
                return 'BackOrder';
        }

        return 'InStock';
    }

    function tryGetColor(sku: Sku): string {
        let color: string = '';
        const { optionGroups } = props.product;
        const { optionUids } = sku;

        if (optionGroups && optionUids) {
            const colorOpt = getColorOptFromSkuOptUids(optionGroups, optionUids);
            color = colorOpt?.value.name || '';
        }

        return color;
    }

    function getReviewObj(respObj: Nullable<ReviewsRawResponse>): ProductReview {
        const review: ProductReview = {
            '@type': 'Review'
        };

        if (!respObj) {
            return review;
        }

        const [reviewsObj, summary] = respObj;
        
        review.aggregateRating = {
            '@type': 'AggregateRating',
            reviewCount: summary.total,
            ratingValue: summary.average
        }

        const revItem = reviewsObj.data?.[0];

        if (!revItem) {
            return review;
        }
    
        return Object.assign(review, {
            reviewRating: {
                '@type': 'Rating',
                'ratingValue': revItem.rating
            },
            author: {
                '@type': 'Person',
                'name': revItem.author
            }
        });
    }

    function getImages(images: FullImage[]): ImageObject[] {
        return images.reduce((acc: ImageObject[], fullImg: FullImage) => {
            if (fullImg.url) {
                acc.push({
                    '@type': 'ImageObject',
                    caption: fullImg.alt,
                    image: fullImg.url
                });
            }

            return acc;
        }, []);
    }
</script>

<style lang="scss"></style>
